<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <div class="page-content">
      <AppBreadcrumb
        v-if="!selectedTicket"
        :items="[{ label: pageTitle }]"
      >
         <!-- Faqat tozalash tugmasi qo'shilgan -->
        <template #actions>
          <a-popconfirm
            title="Barcha bilet natijalarini tozalashni xohlaysizmi? Bu amalni qaytarib bo'lmaydi!"
            ok-text="Ha, tozalash"
            cancel-text="Bekor qilish"
            @confirm="clearAllTicketResults"
            placement="bottomRight"
          >
            <a-button 
              danger 
              type="primary" 
              :loading="clearing"
              :disabled="!apiTickets.length"
              size="small"
            >
              <template #icon><DeleteOutlined /></template>
              Tozalash
            </a-button>
          </a-popconfirm>
        </template>
      </AppBreadcrumb>

      <template v-if="!selectedTicket">
        <a-alert
          v-if="errorMessage"
          type="error"
          show-icon
          :message="errorMessage"
          style="margin-bottom: 16px"
        />

        <TicketList
          :tickets="normalizedTickets"
          :loading="loading"
          @select="startTicket"
        />
      </template>

      <template v-else>
        <TicketTestRoom
          :title="selectedTicket.title"
          :questions="selectedTicket.questions"
          :resultId="selectedTicket.resultId"
          :backRoute="`/tickets/${type}`"
          :pageTitle="pageTitle"
          :ticketType="Number(type)"
          :ticketNumber="selectedTicket.ticketNumber"
          @back="goBackToTickets"
          @go-list="goBackToTickets"
          @finished="clearTicketSession"
        />
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { message } from 'ant-design-vue'
import { DeleteOutlined } from '@ant-design/icons-vue'
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import TicketList from '../components/TicketList.vue'
import TicketTestRoom from '../components/TicketTestRoom.vue'

export default {
  name: 'TicketCategoryView',

  components: {
    HeaderComponent,
    AppBreadcrumb,
    TicketList,
    TicketTestRoom,
    DeleteOutlined
  },

  data() {
    return {
      selectedTicket: null,
      loading: false,
      clearing: false,
      errorMessage: '',
      backendUrl: 'https://avtotesting.uz/api',
      apiTickets: []
    }
  },

  computed: {
    type() {
      return this.$route.params.type
    },

    ticketNumberFromRoute() {
      const n = Number(this.$route.params.ticketNumber)
      return Number.isFinite(n) && n > 0 ? n : null
    },

    normalizedTickets() {
      return (this.apiTickets || []).map((ticket, index) => ({
        ...ticket,
        id: ticket.ticket_number || index + 1,
        number: ticket.ticket_number || index + 1,
        ticketNumber: ticket.ticket_number || index + 1,
        title: `${this.type} talik bilet #${ticket.ticket_number || index + 1}`
      }))
    },

    pageTitle() {
      const types = {
        '10': '10 talik biletlar',
        '20': '20 talik biletlar',
        '50': '50 talik biletlar'
      }
      return types[this.type] || 'Biletlar'
    }
  },

  watch: {
    '$route.params.type': {
      immediate: true,
      async handler() {
        this.selectedTicket = null
        await this.fetchTicketNumbers()
      }
    },

    '$route.params.ticketNumber': {
      immediate: false,
      async handler(newVal, oldVal) {
        if (newVal === oldVal) return
        if (!this.apiTickets.length) return

        const routeTicketNumber = Number(newVal)

        if (!routeTicketNumber) {
          this.selectedTicket = null
          return
        }

        if (this.selectedTicket?.ticketNumber === routeTicketNumber) return

        await this.restoreOrStartTicketByRoute(routeTicketNumber)
      }
    }
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('access_token') || null
      const headers = { 'Content-Type': 'application/json' }

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      return headers
    },

    getSessionStorageKey(ticketNumber = this.ticketNumberFromRoute) {
      return `ticket_session_${this.type}_${ticketNumber}`
    },

    saveSelectedTicketMeta(ticket) {
      if (!ticket?.ticketNumber) return
      localStorage.setItem(
        this.getSessionStorageKey(ticket.ticketNumber),
        JSON.stringify({
          selectedTicket: {
            title: ticket.title,
            ticketNumber: ticket.ticketNumber,
            resultId: ticket.resultId,
            questions: ticket.questions || []
          }
        })
      )
    },

    readTicketSession(ticketNumber = this.ticketNumberFromRoute) {
      try {
        const raw = localStorage.getItem(this.getSessionStorageKey(ticketNumber))
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    },

    clearTicketSession(ticketNumber = this.ticketNumberFromRoute) {
      if (!ticketNumber) return
      localStorage.removeItem(this.getSessionStorageKey(ticketNumber))
    },

    async fetchTicketNumbers() {
      this.loading = true
      this.errorMessage = ''
      this.apiTickets = []

      try {
        const ticketType = Number(this.type)

        if (![10, 20, 50].includes(ticketType)) {
          throw new Error("Faqat 10, 20 yoki 50 talik bilet qo'llab-quvvatlanadi")
        }

        const response = await axios.post(
          `${this.backendUrl}/v1/tests/ticket-numbers/`,
          { ticket_type: ticketType },
          { headers: this.getAuthHeaders() }
        )

        const payload = response.data || {}
        this.apiTickets = payload.tickets || []

        if (!this.apiTickets.length) {
          this.errorMessage = payload.detail || 'Biletlar topilmadi'
          return
        }

        if (this.ticketNumberFromRoute) {
          await this.restoreOrStartTicketByRoute(this.ticketNumberFromRoute)
        }
      } catch (error) {
        console.error('fetchTicketNumbers xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          'Biletlarni olishda xatolik'
      } finally {
        this.loading = false
      }
    },

    async restoreOrStartTicketByRoute(ticketNumber) {
      const saved = this.readTicketSession(ticketNumber)

      if (saved?.selectedTicket?.ticketNumber === ticketNumber) {
        this.selectedTicket = saved.selectedTicket
        return
      }

      const ticket = this.normalizedTickets.find(
        (item) => Number(item.ticketNumber) === Number(ticketNumber)
      )

      if (ticket) {
        await this.startTicket(ticket, { pushRoute: false })
      } else {
        this.errorMessage = `Bilet #${ticketNumber} topilmadi`
        this.$router.replace(`/tickets/${this.type}`)
      }
    },

    // Barcha ticket natijalarini tozalash
    async clearAllTicketResults() {
      this.clearing = true
      
      try {
        const response = await axios.delete(
          `${this.backendUrl}/v1/tests/clear-ticket-results/`,
          { 
            headers: this.getAuthHeaders(),
            data: { ticket_type: Number(this.type) }
          }
        )

        if (response.data) {
          message.success(response.data.message || 'Barcha bilet natijalari tozalandi')
          
          // Biletlar ro'yxatini yangilash
          await this.fetchTicketNumbers()
        }
      } catch (error) {
        console.error('Clear results error:', error)
        message.error(error.response?.data?.detail || 'Natijalarni tozalashda xatolik yuz berdi')
      } finally {
        this.clearing = false
      }
    },

    async startTicket(ticket, options = {}) {
      if (this.loading) return

      const { pushRoute = true } = options

      this.loading = true
      this.errorMessage = ''

      try {
        const ticketNumber =
          ticket.ticketNumber ||
          ticket.ticket_number ||
          ticket.number ||
          ticket.id

        const response = await axios.post(
          `${this.backendUrl}/v1/tests/start-ticket/`,
          {
            ticket_type: Number(this.type),
            ticket_number: Number(ticketNumber)
          },
          {
            headers: this.getAuthHeaders()
          }
        )

        const data = response.data || {}
        const result = data.result || {}
        const questions = data.questions || []

        this.selectedTicket = {
          title: ticket.title || `${this.type} talik bilet #${ticketNumber}`,
          ticketNumber: Number(ticketNumber),
          resultId: result.id,
          questions
        }

        this.saveSelectedTicketMeta(this.selectedTicket)

        if (pushRoute) {
          this.$router.push(`/tickets/${this.type}/${ticketNumber}`)
        }
      } catch (error) {
        console.error('startTicket xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          'Testni boshlashda xatolik'
      } finally {
        this.loading = false
      }
    },

    goBackToTickets() {
      if (this.selectedTicket?.ticketNumber) {
        this.clearTicketSession(this.selectedTicket.ticketNumber)
      }

      this.selectedTicket = null
      this.errorMessage = ''

      this.$router.push(`/tickets/${this.type}`)
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
</style>

<style>
.ant-message.css-dev-only-do-not-override-1p3hq3p {
    z-index: 99999 !important;
}
.ant-popconfirm-buttons span{
  color: #010101;
}
</style>